import { Checkbox, Table } from 'react-bootstrap';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import CheckboxField from 'components/forms/checkboxField/CheckboxField';
import { Property, PropertyBusinessType } from 'models/Properties';
import { AssistPropertiesSelectFormValues } from './AssistPropertiesSelectModal.schema';
import { AssistPropertiesSelectTableContainer } from './AssistPropertiesSelectTable.styles';

const SELECT_ALL_ID = 'assist-properties-select-all';

interface AssistPropertiesSelectTableProps {
  properties: Property[];
}

const AssistPropertiesSelectTable = ({
  properties,
}: AssistPropertiesSelectTableProps) => {
  const { t } = useTranslation();
  const { control, setValue } =
    useFormContext<AssistPropertiesSelectFormValues>();

  const checkedProperties = useWatch({ control, name: 'properties' });

  const allSelected =
    !!properties.length &&
    properties.every(({ uid }) => checkedProperties[uid]);

  const handleSelectAll = () => {
    setValue(
      'properties',
      properties.reduce(
        (acc, { uid }) => ({ ...acc, [uid]: !allSelected }),
        {} as AssistPropertiesSelectFormValues['properties'],
      ),
      { shouldDirty: true },
    );
  };

  const formatPropertyName = (property: Property) => {
    if (property.businessType === PropertyBusinessType.HOTEL) {
      return t('pageHostfullyAssist.details.propertiesSelectModal.list.hotel', {
        name: property.name,
      });
    }

    if (
      property.businessType === PropertyBusinessType.STANDALONE_PROPERTY &&
      property.numberOfSubUnits > 0
    ) {
      return t(
        'pageHostfullyAssist.details.propertiesSelectModal.list.master',
        {
          name: property.name,
        },
      );
    }

    return property.name;
  };

  return (
    <AssistPropertiesSelectTableContainer>
      <Table>
        <caption>
          {t('pageHostfullyAssist.details.propertiesSelectModal.tableCaption')}
        </caption>
        <thead>
          <tr>
            <th>
              <Checkbox
                id={SELECT_ALL_ID}
                data-testid={SELECT_ALL_ID}
                checked={allSelected}
                onChange={handleSelectAll}
              />
            </th>
            <th>
              <label htmlFor={SELECT_ALL_ID}>
                {t(
                  'pageHostfullyAssist.details.propertiesSelectModal.selectAll',
                )}
              </label>
            </th>
          </tr>
        </thead>
        <tbody>
          {properties.map((property) => {
            const checkboxId = `assist-property-checkbox-${property.uid}`;

            return (
              <tr key={property.uid}>
                <td>
                  <CheckboxField
                    name={`properties.${property.uid}`}
                    id={checkboxId}
                    data-testid={checkboxId}
                  />
                </td>
                <td>
                  <label htmlFor={checkboxId}>
                    {formatPropertyName(property)}
                  </label>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </AssistPropertiesSelectTableContainer>
  );
};

export default AssistPropertiesSelectTable;
